import { AnswerOptionType } from '@/client/quizzes';
import {
  SecurityProfileQuiz,
  SecurityProfileResponse,
} from '@/client/security-profile';
import { useSecurityProfileText } from '@/hooks/useSecurityProfileText';
import CheckSvg from '@/images/security-profile/check.svg';
import ShieldIconSvg from '@/images/security-profile/emergency.svg';
import NoteSvg from '@/images/security-profile/note.svg';
import DiamondAlertSvg from '@/images/security-profile/shield.svg';
import { AppButton } from '@/ui/buttons';
import {
  SecurityProfileDescription,
  SecurityProfileIconContainer,
  SecurityProfileLayout,
  SecurityProfileMetadata,
  SecurityProfileTitle,
} from '@/ui/security-profile-layout';
import { formatDate } from '@/utils/date/date.helper';
import { modifyCourseQuiz } from '@/utils/helpers/courses.helper';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface AnswerDisplayItem {
  text: string;
  isCustom: boolean;
  customLabel?: string;
  customValue?: string;
}

interface SecurityProfileSummaryProps {
  quiz: SecurityProfileQuiz;
  responses: SecurityProfileResponse[];
  onEditQuestion: (questionIndex: number) => void;
}

const StyledQuestionCard = styled.div`
  background: var(--white-main);
  border-radius: var(--medium-border-radius);
  border: 1px solid var(--gray-light);
  padding: var(--default-padding);
  margin-bottom: var(--default-padding);
  display: flex;
  flex-direction: column;

  .question-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--small-padding);
  }

  .question-icon-container {
    width: 38px;
    height: 38px;
    border-radius: 9px;
    background-color: var(--purple-middle);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: var(--small-padding);
    flex-shrink: 0;
  }

  .question-icon {
    width: 20px;
    height: 20px;
  }

  .question-title {
    font-size: var(--medium-font-size);
    line-height: var(--small-line-height);
    font-weight: 600;
    color: var(--black-main);
    flex: 1;
    margin-right: var(--small-padding);
  }

  .question-content {
    margin-left: 0;
  }

  .your-answer {
    font-size: var(--xsmall-font-size);
    font-weight: 600;
    margin-bottom: var(--small-padding);
  }

  .answer-list {
    margin-bottom: 0;
    padding-left: var(--medium-padding);

    li {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      margin-bottom: var(--xsmall-padding);
      color: var(--black-main);

      &:last-child {
        margin-bottom: 0;
      }

      &::marker {
        color: var(--red-main);
        font-size: 0.7em;
      }
    }
  }

  .no-answer {
    font-size: var(--small-font-size);
    color: var(--gray-darker);
    font-style: italic;
  }
`;

export const SecurityProfileSummary: React.FC<SecurityProfileSummaryProps> = ({
  quiz,
  responses,
  onEditQuestion,
}) => {
  const { t } = useTranslation();
  const { getQuestionText, getAnswerOptionText } = useSecurityProfileText();

  const adaptedQuiz = useMemo(() => modifyCourseQuiz(quiz), [quiz]);

  const getQuestionIconSrc = useCallback((index: number): string => {
    const icons = [DiamondAlertSvg, ShieldIconSvg, NoteSvg, CheckSvg];
    return icons[index % icons.length];
  }, []);

  const formatAnswerDisplay = useCallback(
    (questionId: string): AnswerDisplayItem[] => {
      const response = responses.find((r) => r.questionId === questionId);
      if (!response) return [];

      const question = adaptedQuiz?.questions.find((q) => q.id === questionId);
      if (!question) return [];

      const formattedAnswers: AnswerDisplayItem[] = [];

      const hasCustomTextResponse =
        response.textResponse && response.textResponse.trim();

      if (response.selectedAnswerIds && response.selectedAnswerIds.length > 0) {
        response.selectedAnswerIds.forEach((answerId) => {
          const answerOption = question.answerOptions?.find(
            (ao) => ao.id === answerId,
          );
          if (answerOption) {
            const answerText = getAnswerOptionText(question, answerOption);
            if (answerText) {
              if (
                hasCustomTextResponse &&
                answerOption.type === AnswerOptionType.CUSTOM_INPUT
              ) {
                return;
              }
              formattedAnswers.push({
                text: answerText,
                isCustom: false,
              });
            }
          }
        });
      }

      if (hasCustomTextResponse) {
        const customAnswerOption = question.answerOptions?.find(
          (ao) =>
            ao.type === AnswerOptionType.CUSTOM_INPUT &&
            response.selectedAnswerIds.includes(ao.id || ''),
        );
        const customAnswerText = getAnswerOptionText(
          question,
          customAnswerOption,
        );

        formattedAnswers.push({
          text: `${customAnswerText}: ${response.textResponse}`,
          isCustom: true,
          customLabel: customAnswerText,
          customValue: response.textResponse,
        });
      }

      return formattedAnswers;
    },
    [responses, adaptedQuiz, getAnswerOptionText],
  );

  const leftContent = (
    <>
      <SecurityProfileIconContainer>
        <img src={CheckSvg} alt="Completed" />
      </SecurityProfileIconContainer>
      <SecurityProfileTitle>
        {t('securityProfile.complete.title')}
      </SecurityProfileTitle>
      <SecurityProfileDescription>
        {t('securityProfile.complete.thankYou')}
      </SecurityProfileDescription>
      <SecurityProfileDescription>
        {t('securityProfile.complete.valueProposition')}
      </SecurityProfileDescription>
      {quiz.updatedAt && (
        <SecurityProfileMetadata>
          {t('securityProfile.complete.lastUpdated', {
            date: formatDate(quiz.updatedAt, 'll'),
          })}
          {quiz.updatedByName && (
            <>
              <br />
              {t('securityProfile.complete.updatedBy', {
                name: quiz.updatedByName,
              })}
            </>
          )}
        </SecurityProfileMetadata>
      )}
    </>
  );

  const renderedQuestions = useMemo(() => {
    return (adaptedQuiz?.questions ?? []).map((question, index) => {
      const questionText = getQuestionText(question);
      const answers = formatAnswerDisplay(question.id || '');
      const questionIconSrc = getQuestionIconSrc(index);

      return (
        <StyledQuestionCard key={question.id}>
          <div className="question-header">
            <div className="question-icon-container">
              <img
                src={questionIconSrc}
                className="question-icon"
                alt="Question icon"
              />
            </div>
            <div className="question-title">{questionText}</div>
            <AppButton
              label={t('securityProfile.summary.editAnswer')}
              type="outlined"
              size="sm"
              onClick={() => onEditQuestion(index)}
            />
          </div>
          <div className="question-content">
            <div className="your-answer">
              {t('securityProfile.summary.yourAnswer')}
            </div>
            {answers.length > 0 ? (
              <ul className="answer-list">
                {answers.map((answer, i) => (
                  <li key={i}>
                    {answer.isCustom ? (
                      <>
                        <strong>{answer.customLabel}:</strong>{' '}
                        {answer.customValue}
                      </>
                    ) : (
                      answer.text
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="no-answer">
                {t('securityProfile.summary.noAnswer')}
              </div>
            )}
          </div>
        </StyledQuestionCard>
      );
    });
  }, [adaptedQuiz, t, onEditQuestion, formatAnswerDisplay, getQuestionIconSrc]);

  const rightContent = <>{renderedQuestions}</>;

  return (
    <SecurityProfileLayout
      leftContent={leftContent}
      rightContent={rightContent}
    />
  );
};
